import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { calculateChargingFee } from '../../utils/feeCalculator';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';

interface PriceSchedule {
  startTime: string;
  endTime: string;
  price: number;
}

interface Transaction {
  _id: string;
  transactionId: string;
  connectorId: number;
  userId?: {
    _id: string;
    name: string;
    email: string;
  };
  chargePointId: {
    _id: string;
    name: string;
    chargePointId: string;
    prices: PriceSchedule[];
    operatorId: {
      name: string;
      currency: string;
    };
  };
  rfidTagId: {
    _id: string;
    tagId: string;
    type: 'personal' | 'public';
  };
  startTime: string;
  endTime?: string;
  meterStart: number;
  meterStop?: number;
  totalEnergy: number;
  totalCost: number;
  status: 'active' | 'suspended' | 'completed' | 'failed';
  updatedAt?: string;
}

const TransactionList: React.FC = () => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Add pagination and filter states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    operatorId: ''
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Store the full dataset for client-side pagination
  const [fullDataset, setFullDataset] = useState<Transaction[] | null>(null);
  const [isClientPagination, setIsClientPagination] = useState(false);
  
  // Add state for operators
  const [operators, setOperators] = useState<{_id: string, name: string, company: string}[]>([]);
  
  // Table container ref for scroll detection
  const tableContainerRef = useRef<HTMLDivElement>(null);
  // Track if component is mounted (for cleanup)
  const isMounted = useRef(true);
  // Keep track of latest transaction timestamp for polling
  const lastPolled = useRef<Date>(new Date());
  // Keep track of transaction IDs to detect new ones
  const knownTransactionIds = useRef<Set<string>>(new Set());
  // Keep track of active transaction IDs to detect status changes
  const activeTransactionIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Set the mount state
    isMounted.current = true;
    
    // Initial fetch with explicit pagination
    fetchTransactions(1, 20);
    
    // Fetch operators for the filter
    fetchOperators();
    
    // Setup global polling for transaction updates
    const interval = setInterval(() => {
      if (isMounted.current) {
        pollForTransactionUpdates();
      }
    }, 5000);
    
    // Cleanup on unmount
    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, []);

  // Add function to fetch operators
  const fetchOperators = async () => {
    try {
      console.log('Fetching operators...');
      const response = await api.get('/operators');
      console.log('Operators response:', response.data);
      setOperators(response.data);
    } catch (err) {
      console.error('Failed to fetch operators:', err);
      toast.error(t('operators.messages.fetchError'));
    }
  };

  // Comprehensive polling for all transaction updates
  const pollForTransactionUpdates = async () => {
    try {
      // Always check for active transactions updates (meter values, status changes)
      // plus any new transactions that might have been created since last poll
      console.log('Polling for transaction updates...');
      
      const activeStatusFilter = { ...filters, status: 'active' };
      
      // Get all active transactions to check for meter updates and status changes
      const activeResponse = await api.get('/transactions', { 
        params: activeStatusFilter
      });
      
      const activeTransactions = Array.isArray(activeResponse.data) 
        ? activeResponse.data 
        : [];
        
      console.log(`Found ${activeTransactions.length} active transactions`);
      
      // Check if we should refresh the whole list
      let shouldRefreshList = false;
      
      // Check if any active transactions are not in our current list (new ones)
      const currentIds = new Set(transactions.map(t => t._id));
      const newTransactions = activeTransactions.filter(t => !currentIds.has(t._id));
      
      if (newTransactions.length > 0) {
        console.log(`Found ${newTransactions.length} new transactions`);
        shouldRefreshList = true;
      }
      
      // Check if any transactions we know about changed status
      const statusChanges = activeTransactions.filter(active => {
        const existing = transactions.find(t => t._id === active._id);
        return existing && existing.status !== active.status;
      });
      
      if (statusChanges.length > 0) {
        console.log(`Found ${statusChanges.length} status changes`);
        shouldRefreshList = true;
      }
      
      // Get the current active transaction IDs
      const currentActiveIds = new Set(activeTransactions.map(t => t._id));
      
      // Check if any previously active transactions are no longer active (completed/failed)
      const completedTransactions = Array.from(activeTransactionIds.current)
        .filter(id => !currentActiveIds.has(id));
      
      if (completedTransactions.length > 0) {
        console.log(`Detected ${completedTransactions.length} transactions that became inactive/completed`);
        shouldRefreshList = true;
      }
      
      // Update our record of active transaction IDs
      activeTransactionIds.current = currentActiveIds;
      
      // If we should refresh the whole list based on what we found
      if (shouldRefreshList) {
        console.log('Refreshing transaction list due to new/changed transactions');
        fetchTransactions(1, pagination.limit, filters);
        return;
      }
      
      // Otherwise, just update the meter values for active transactions
      if (activeTransactions.length > 0) {
        setTransactions(prev => {
          const updatedList = [...prev];
          
          activeTransactions.forEach(active => {
            const index = updatedList.findIndex(t => t._id === active._id);
            if (index !== -1) {
              // Only update if there's a change in meter values or total energy
              if (active.meterStop !== updatedList[index].meterStop || 
                  active.totalEnergy !== updatedList[index].totalEnergy ||
                  active.totalCost !== updatedList[index].totalCost) {
                console.log(`Updating transaction ${active._id} with new meter values`);
                updatedList[index] = active;
              }
            }
          });
          
          return updatedList;
        });
        
        // Also update full dataset if we're using client-side pagination
        if (isClientPagination && fullDataset) {
          setFullDataset(prev => {
            if (!prev) return prev;
            
            const updatedDataset = [...prev];
            activeTransactions.forEach(active => {
              const index = updatedDataset.findIndex(t => t._id === active._id);
              if (index !== -1) {
                updatedDataset[index] = active;
              }
            });
            
            return updatedDataset;
          });
        }
      }
      
    } catch (err) {
      console.error('Error polling for transaction updates:', err);
    }
  };

  // Add scroll handler for infinite scrolling
  const handleTableScroll = useCallback(() => {
    if (!tableContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    const scrollPosition = scrollTop + clientHeight;
    const scrollThreshold = scrollHeight - 200; // Load more when 200px from bottom
    
    if (scrollPosition >= scrollThreshold && !isLoadingMore && pagination.page < pagination.totalPages) {
      loadMoreItems();
    }
  }, [isLoadingMore, pagination]);

  // Load more function for infinite scrolling
  const loadMoreItems = useCallback(() => {
    if (pagination.page >= pagination.totalPages) return;
    
    if (isClientPagination && fullDataset) {
      // Handle client-side pagination
      const nextPage = pagination.page + 1;
      const startIndex = (nextPage - 1) * pagination.limit;
      const endIndex = startIndex + pagination.limit;
      
      // Get next page of items from the full dataset
      const nextPageItems = fullDataset.slice(startIndex, Math.min(endIndex, fullDataset.length));
      
      // Update the transactions with next page items
      setTransactions(prev => [...prev, ...nextPageItems]);
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        page: nextPage
      }));
    } else {
      // Use server-side pagination
      fetchTransactions(pagination.page + 1, pagination.limit, filters);
    }
  }, [pagination, filters, fullDataset, isClientPagination]);

  const fetchTransactions = async (page: number, limit: number = 20, currentFilters = filters) => {
    try {
      console.log(`Fetching transactions: page=${page}, limit=${limit}, filters=`, currentFilters);
      setIsLoadingMore(page > 1);
      setLoading(page === 1);
      
      // Build params object based on available filters
      const params: any = {
        page,
        limit
      };
      
      if (currentFilters.status) params.status = currentFilters.status;
      if (currentFilters.startDate) params.startDate = currentFilters.startDate;
      if (currentFilters.endDate) params.endDate = currentFilters.endDate;
      if (currentFilters.operatorId) params.operatorId = currentFilters.operatorId;
      
      const response = await api.get('/transactions', { params });
      console.log('API response:', response.data);
      
      // Reset the last polled time
      lastPolled.current = new Date();
      
      // Extract transactions data from response
      const transactionData = Array.isArray(response.data) ? response.data : [];
      
      // Update known transaction IDs
      knownTransactionIds.current = new Set(
        transactionData.map((t: Transaction) => t._id)
      );
      
      // Update active transaction IDs set for status change detection
      if (page === 1) {
        // Find all active transactions in the fetched data
        const activeIds = transactionData
          .filter((t: Transaction) => t.status === 'active')
          .map((t: Transaction) => t._id);
        
        // Update the ref with these active IDs
        activeTransactionIds.current = new Set(activeIds);
        console.log(`Tracking ${activeIds.length} active transactions for status changes`);
      } else {
        // For pagination, add to the existing set instead of replacing
        transactionData
          .filter((t: Transaction) => t.status === 'active')
          .forEach((t: Transaction) => {
            activeTransactionIds.current.add(t._id);
          });
      }
      
      // Handle client-side pagination
      const isFirstPage = page === 1;
      
      // Detect if server returned the entire dataset at once
      const isEntireSet = !response.data.pagination && 
                          isFirstPage && 
                          transactionData.length > limit;
      
      // If first page and entire dataset, set up client-side pagination
      if (isFirstPage) {
        setIsClientPagination(isEntireSet);
        
        if (isEntireSet) {
          // Store full dataset for client-side pagination
          setFullDataset(transactionData);
        } else {
          // Clear full dataset if we're using server pagination
          setFullDataset(null);
        }
      }
      
      // For client-side pagination, slice the data to the current page
      let pagedTransactionData = transactionData;
      if (isEntireSet) {
        const start = (page - 1) * limit;
        const end = start + limit;
        pagedTransactionData = transactionData.slice(start, end);
      }
      
      // Update transactions state
      if (page === 1) {
        setTransactions(pagedTransactionData);
      } else if (pagedTransactionData.length > 0) {
        setTransactions(prev => [...prev, ...pagedTransactionData]);
      }
      
      // Calculate correct pagination values
      let totalRecords = 0;
      let totalPageCount = 0;
      
      if (response.data.pagination) {
        // Use server-provided pagination if available
        totalRecords = response.data.pagination.total;
        totalPageCount = response.data.pagination.totalPages;
      } else if (isEntireSet) {
        // If we have the entire dataset, calculate based on full dataset
        totalRecords = transactionData.length;
        totalPageCount = Math.ceil(totalRecords / limit);
      } else if (isFirstPage && transactionData.length < limit) {
        // If we got less than a page on first request, that's all there is
        totalRecords = transactionData.length;
        totalPageCount = 1;
      } else {
        // Estimate based on current data
        const knownRecords = (page - 1) * limit + transactionData.length;
        const hasMore = transactionData.length === limit; // Full page means probably more
        totalRecords = hasMore ? knownRecords + limit : knownRecords;
        totalPageCount = Math.ceil(totalRecords / limit);
      }
      
      // Update pagination info
      setPagination({
        page,
        limit,
        total: totalRecords,
        totalPages: totalPageCount > 0 ? totalPageCount : 1
      });
      
      setLoading(false);
      setIsLoadingMore(false);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(t('transactions.messages.fetchError'));
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Add debounced date filter
  const debouncedDateFilter = useCallback(
    debounce((dateFilters: {startDate?: string, endDate?: string}) => {
      const newFilters = { ...filters, ...dateFilters };
      setFilters(newFilters);
      setPagination(prev => ({ ...prev, page: 1 }));
      
      // Clear client-side pagination data when filters change
      setIsClientPagination(false);
      setFullDataset(null);
      
      fetchTransactions(1, pagination.limit, newFilters);
    }, 500),
    [filters, pagination.limit]
  );

  const formatPrice = (amount: number, currency: string) => {
    if (currency === 'USD') {
      return `$${amount.toFixed(2)}`;
    } else {
      return `${amount.toLocaleString('vi-VN')}đ`;
    }
  };

  const calculateCurrentFee = (transaction: Transaction) => {
    if (transaction.status === 'active') {
      if (!transaction.chargePointId?.prices || transaction.chargePointId.prices.length === 0) {
        return transaction.totalCost || 0;
      }

      const currentMeterValue = transaction.meterStop || transaction.meterStart;
      const energyConsumed = (currentMeterValue - transaction.meterStart) / 1000; // Convert to kWh
      
      try {
        const fee = calculateChargingFee(
          new Date(transaction.startTime),  // Start time from transaction
          new Date(),                       // Current time for ongoing transactions
          transaction.chargePointId.prices, // Price schedules from charge point
          energyConsumed                    // Energy consumed in kWh
        );
        return fee;
      } catch (error) {
        console.error('Error calculating fee:', error);
        return transaction.totalCost || 0;
      }
    }
    return transaction.totalCost || 0;
  };

  // Add status badge color function
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  // Reset all filters and pagination
  const resetAllFilters = () => {
    const resetFilters = {
      status: '',
      startDate: '',
      endDate: '',
      operatorId: ''
    };
    
    setFilters(resetFilters);
    setIsClientPagination(false);
    setFullDataset(null);
    fetchTransactions(1, pagination.limit, resetFilters);
  };

  // Add filters display
  const renderFilters = () => (
    <div className="mb-4 grid grid-cols-1 md:grid-cols-5 gap-4">
      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1">
          {t('transactions.filters.status')}
        </label>
        <select
          className="w-full px-3 py-2 border rounded"
          value={filters.status}
          onChange={(e) => {
            const newFilters = { ...filters, status: e.target.value };
            setFilters(newFilters);
            setPagination(prev => ({ ...prev, page: 1 }));
            
            // Clear client-side pagination data when filters change
            setIsClientPagination(false);
            setFullDataset(null);
            
            fetchTransactions(1, pagination.limit, newFilters);
          }}
        >
          <option value="">{t('transactions.filters.allStatuses')}</option>
          <option value="active">{t('transactions.status.active')}</option>
          <option value="completed">{t('transactions.status.completed')}</option>
          <option value="suspended">{t('transactions.status.suspended')}</option>
          <option value="failed">{t('transactions.status.failed')}</option>
        </select>
      </div>
      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1">
          {t('transactions.filters.operator')}
        </label>
        <select
          className="w-full px-3 py-2 border rounded"
          value={filters.operatorId}
          onChange={(e) => {
            const newFilters = { ...filters, operatorId: e.target.value };
            setFilters(newFilters);
            setPagination(prev => ({ ...prev, page: 1 }));
            
            // Clear client-side pagination data when filters change
            setIsClientPagination(false);
            setFullDataset(null);
            
            fetchTransactions(1, pagination.limit, newFilters);
          }}
        >
          <option value="">{t('transactions.filters.allOperators')}</option>
          {operators.map(operator => (
            <option key={operator._id} value={operator._id}>
              {operator.name} ({operator.company})
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1">
          {t('transactions.filters.startDate')}
        </label>
        <input
          type="date"
          className="w-full px-3 py-2 border rounded"
          value={filters.startDate}
          onChange={(e) => {
            debouncedDateFilter({ startDate: e.target.value });
          }}
        />
      </div>
      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1">
          {t('transactions.filters.endDate')}
        </label>
        <input
          type="date"
          className="w-full px-3 py-2 border rounded"
          value={filters.endDate}
          onChange={(e) => {
            debouncedDateFilter({ endDate: e.target.value });
          }}
        />
      </div>
      <div className="flex items-end">
        <button
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          onClick={resetAllFilters}
        >
          {t('transactions.filters.reset')}
        </button>
      </div>
    </div>
  );

  // Add count display
  const renderCountSummary = () => {
    let filterDescription = '';
    const activeFilters = [];
    
    if (filters.status) {
      activeFilters.push(t(`transactions.status.${filters.status}`));
    }
    
    if (filters.operatorId) {
      const operator = operators.find(op => op._id === filters.operatorId);
      if (operator) {
        activeFilters.push(`${t('transactions.filters.operator')}: ${operator.name} (${operator.company})`);
      } else {
        activeFilters.push(`${t('transactions.filters.operator')}: ${filters.operatorId}`);
      }
    }
    
    if (filters.startDate && filters.endDate) {
      activeFilters.push(
        `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`
      );
    } else if (filters.startDate) {
      activeFilters.push(`${t('transactions.filters.from')} ${new Date(filters.startDate).toLocaleDateString()}`);
    } else if (filters.endDate) {
      activeFilters.push(`${t('transactions.filters.until')} ${new Date(filters.endDate).toLocaleDateString()}`);
    }
    
    if (activeFilters.length > 0) {
      filterDescription = activeFilters.join(', ');
    }
    
    // Count active transactions
    const activeCount = transactions.filter(t => t.status === 'active').length;
    
    return (
      <div className="mb-4 text-gray-600 font-medium">
        {filterDescription ? (
          <p>
            {t(
              'transactions.filterResults', 
              `Showing ${transactions.length} of ${pagination.total} transactions (${filterDescription})`,
              { 
                showing: transactions.length,
                total: pagination.total, 
                filters: filterDescription
              }
            )}
          </p>
        ) : (
          <p>
            {t(
              'transactions.totalCount', 
              `Showing ${transactions.length} of ${pagination.total} total transactions`,
              { showing: transactions.length, total: pagination.total }
            )}
          </p>
        )}
        {activeCount > 0 && (
          <p className="mt-1 text-blue-600">
            {t('transactions.activeCount', { count: activeCount })}
          </p>
        )}
      </div>
    );
  };

  if (loading && transactions.length === 0) return <div>{t('common.loading')}</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">{t('transactions.title')}</h2>
      
      {/* Add filters and count summary */}
      {renderFilters()}
      {renderCountSummary()}
      
      <div 
        ref={tableContainerRef} 
        className="overflow-x-auto h-[600px] overflow-y-auto"
        onScroll={handleTableScroll}
      >
        <table className="min-w-full bg-white">
          <thead className="sticky top-0 bg-white shadow z-10">
            <tr>
              <th className="border px-4 py-2">{t('transactions.columns.user')}</th>
              <th className="border px-4 py-2">{t('transactions.columns.chargePoint')}</th>
              <th className="border px-4 py-2">{t('transactions.columns.startTime')}</th>
              <th className="border px-4 py-2">{t('transactions.columns.endTime')}</th>
              <th className="border px-4 py-2">{t('transactions.columns.energy')}</th>
              <th className="border px-4 py-2">{t('transactions.columns.fee')}</th>
              <th className="border px-4 py-2">{t('transactions.columns.status')}</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length > 0 ? (
              transactions.map((transaction) => (
              <tr key={transaction._id}>
                <td className="border px-4 py-2">
                  {transaction.userId 
                    ? (transaction.userId.name || transaction.userId.email)
                    : `${t('transactions.messages.publicTag')} (${transaction.rfidTagId?.tagId || t('transactions.messages.unknownTag')})`}
                </td>
                <td className="border px-4 py-2">
                  {transaction.chargePointId?.chargePointId 
                      ? `${transaction.chargePointId.name} (${transaction.chargePointId.chargePointId}:${transaction.connectorId})`
                    : t('transactions.messages.unknownCharger')}
                </td>
                <td className="border px-4 py-2">
                  {new Date(transaction.startTime).toLocaleString()}
                </td>
                <td className="border px-4 py-2">
                  {transaction.endTime 
                    ? new Date(transaction.endTime).toLocaleString()
                    : t('transactions.messages.inProgress')}
                </td>
                <td className="border px-4 py-2">
                    {(transaction.totalEnergy || 0).toFixed(2)} kWh
                </td>
                <td className="border px-4 py-2">
                  {formatPrice(
                    transaction.status === 'active' 
                      ? calculateCurrentFee(transaction)
                      : (transaction.totalCost || 0),
                    transaction.chargePointId?.operatorId?.currency || 'VND'
                  )}
                </td>
                <td className="border px-4 py-2">
                    <span className={`px-2 py-1 rounded ${getStatusColor(transaction.status)}`}>
                    {t(`transactions.status.${transaction.status}`)}
                  </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="border px-4 py-8 text-center text-gray-500">
                  {t('transactions.messages.noTransactions')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Add loading indicator and load more trigger at the bottom */}
      <div className="text-center py-4">
        {isLoadingMore ? (
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-2"></div>
            {t('common.loading')}
          </div>
        ) : pagination.page < pagination.totalPages ? (
          <div>
            <button
              onClick={loadMoreItems}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('common.loadMore')}
            </button>
            <div className="mt-2 text-gray-600">
              {t(
                'transactions.showingCount', 
                `Showing ${transactions.length} of ${pagination.total} transactions`,
                { showing: transactions.length, total: pagination.total }
              )}
            </div>
          </div>
        ) : transactions.length > 0 ? (
          <div className="text-gray-600">
            {t('common.allItemsLoaded')}
          </div>
        ) : null}
      </div>
      
      {/* Debug info - can be removed in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-8 p-4 bg-gray-100 rounded text-xs">
          <h4 className="font-bold">Debug Info:</h4>
          <div>Active Transactions: {transactions.filter(t => t.status === 'active').length}</div>
          <div>Tracked Active IDs: {activeTransactionIds.current.size}</div>
          <div>Page: {pagination.page}/{pagination.totalPages}</div>
          <div>Total: {pagination.total}</div>
          <div>Limit: {pagination.limit}</div>
          <div>Client Pagination: {isClientPagination ? 'Yes' : 'No'}</div>
          <div>Full Dataset Size: {fullDataset ? fullDataset.length : 'N/A'}</div>
          <div>Last Polled: {lastPolled.current.toLocaleTimeString()}</div>
        </div>
      )}
    </div>
  );
};

export default TransactionList; 